import React, { useState } from 'react';
import { useModal } from '@gumtree/shell/src/use-modal';
import { LoginModalType } from '@gumtree/shell/src/reducers/common/login-modal';
import postMailImg from '@gumtree/ui-library/src/assets/post-mail.png';
import submitSuccessImg from '@gumtree/ui-library/src/assets/submit-success.png';
import submitFailImg from '@gumtree/ui-library/src/assets/post-fail.png';
import SubmitResultDialog from '@gumtree/ui-library/src/submit-result-dialog/submit-result-dialog';
import { trackGA4Event } from '@gumtree/shared/src/util/ga4-shared';
import { Container, Left, Right, LeftContent, Img, TextBlock, Title, Subtitle, ButtonGrid, AnswerButton } from './index.style';
import { QaItem } from '../../types';
import PostAJobForm from '../post-a-job-form';

interface Props {
    qaList: QaItem[];
    categoryId: string;
    currentPageUrl: string;
    userId?: string;
    requestId: string;
    buyerUrl: string;
}

const PostAJob: React.FC<Props> = ({
    qaList,
    categoryId,
    currentPageUrl,
    userId = '',
    requestId,
    buyerUrl
}) => {
    const { title, answers } = qaList[0];

    const { openDialog } = useModal();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedAnswerId, setSelectedAnswerId] = useState<string>('');
    const [isResultDialogOpen, setIsResultDialogOpen] = useState(false);
    const [resultMessage, setResultMessage] = useState<string>('');
    const [isSuccess, setIsSuccess] = useState<boolean>(true);
    
    const onCloseModal = () => {
        setIsModalOpen(false);
        setSelectedAnswerId('');
    };

    const handleAnswerClick = (answerId: string) => {
        trackGA4Event<any>({
            event: 'ad_click_button_info',
            linkText: 'post a job',
            requestId,
            listingId: ''
        });

        if (!userId || userId.trim() === '' || userId === 'undefined' || userId === 'null') {
            openDialog(undefined, LoginModalType.REQUEST_QUOTE, true);
            return;
        }
        
        setSelectedAnswerId(answerId);
        setIsModalOpen(true);
    };

    const handleSubmitSuccess = (message: string) => {
        setResultMessage(message);
        setIsSuccess(true);
        setIsResultDialogOpen(true);
    };

    const handleSubmitFail = (message: string) => {
        setResultMessage(message);
        setIsSuccess(false);
        setIsResultDialogOpen(true);
    };

    const handleResultDialogClose = () => {
        setIsResultDialogOpen(false);
        setResultMessage('');
        setIsSuccess(true);
        onCloseModal();
    };

    return (
        <Container>
            <Left>
                <LeftContent>
                    <Img src={postMailImg} alt="Post mail" />
                    <TextBlock>
                        <Title>{title}</Title>
                        <Subtitle>Get quotes from providers that match your schedule.</Subtitle>
                    </TextBlock>
                </LeftContent>
            </Left>
            <Right>
                <ButtonGrid>
                    {answers.map((answer) => (
                        <AnswerButton key={answer.answerId} id={answer.answerId} onClick={() => handleAnswerClick(answer.answerId)}>
                            {answer.answerText}
                        </AnswerButton>
                    ))}
                </ButtonGrid>
            </Right>
            {isModalOpen && (
                <PostAJobForm 
                    isModalOpen={isModalOpen} 
                    onCloseModal={onCloseModal}
                    onSubmitSuccess={handleSubmitSuccess}
                    onSubmitFail={handleSubmitFail}
                    qaList={qaList}
                    selectedAnswerId={selectedAnswerId}
                    userId={userId}
                    categoryId={categoryId}
                    currentPageUrl={currentPageUrl}
                    requestId={requestId}
                    buyerUrl={buyerUrl}
                />
            )}
            <SubmitResultDialog
                isOpen={isResultDialogOpen}
                onClose={handleResultDialogClose}
                imageSrc={isSuccess ? submitSuccessImg : submitFailImg}
                imageAlt={isSuccess ? "Success" : "Failed"}
                title={isSuccess ? "Request sent" : "Uh-oh! Something went wrong"}
                description={resultMessage}
                buttonText={isSuccess ? "Got it" : "Try again later"}
                onButtonClick={handleResultDialogClose}
            />
        </Container>
    );
};

export default PostAJob;
